"use client";

import { useEffect } from "react";
import { Icon } from "@astryxdesign/core/Icon";
import { researchIcons } from "@/lib/icons/research-icons";

export type PromoBannerModalStyle = {
  imageRadiusPx: number;
  showCloseButton: boolean;
  maxWidthDesktop: number;
  maxWidthMobile: number;
};

type Props = {
  imageUrl: string;
  isMobile: boolean;
  style: PromoBannerModalStyle;
  busy?: boolean;
  ariaLabel: string;
  error?: string | null;
  onPrimary: () => void;
  onClose: () => void;
};

/**
 * Попап только с баннером: клик по картинке = CTA, крестик/фон = закрыть.
 */
export function PromoBannerModal({
  imageUrl,
  isMobile,
  style,
  busy = false,
  ariaLabel,
  error,
  onPrimary,
  onClose,
}: Props) {
  const maxWidth = isMobile ? style.maxWidthMobile : style.maxWidthDesktop;
  const radius = Math.max(0, Math.min(48, style.imageRadiusPx));

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      onClick={onClose}
    >
      <div
        className="relative w-full min-w-0"
        style={{ maxWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        {style.showCloseButton ? (
          <button
            type="button"
            onClick={onClose}
            className="absolute -right-1 -top-1 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-black/55 text-white shadow-md backdrop-blur-sm transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:-right-2 sm:-top-2"
            aria-label="Закрыть"
          >
            <Icon icon={researchIcons.close} size="sm" />
          </button>
        ) : null}

        <button
          type="button"
          disabled={busy}
          onClick={onPrimary}
          className="block w-full min-w-0 cursor-pointer overflow-hidden border-0 bg-transparent p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          style={{ borderRadius: radius }}
          aria-label={ariaLabel}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={imageUrl}
            alt=""
            className="h-auto max-h-[min(85vh,720px)] w-full object-contain"
            style={{ borderRadius: radius }}
            draggable={false}
          />
        </button>

        {error ? (
          <p className="mt-2 rounded-lg bg-black/70 px-3 py-2 text-center text-sm text-white">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export const DEFAULT_PROMO_BANNER_STYLE: PromoBannerModalStyle = {
  imageRadiusPx: 20,
  showCloseButton: true,
  maxWidthDesktop: 560,
  maxWidthMobile: 360,
};

export function clampPromoRadius(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_PROMO_BANNER_STYLE.imageRadiusPx;
  return Math.max(0, Math.min(48, Math.round(value)));
}

export function clampPromoMaxWidth(
  value: number,
  fallback: number,
  min = 240,
  max = 900,
): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.max(min, Math.min(max, Math.round(value)));
}
